import { useState, useEffect } from 'react';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { motion } from 'framer-motion';
import { Card, CardContent } from '../components/ui/card';
import { ClipboardCheck, CheckCircle2, Hourglass, Users } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const initials = (name) => (name || '?').split(' ').filter(Boolean).slice(0, 2).map((p) => p[0].toUpperCase()).join('');

export default function OnboardingTracker() {
  const { getAuthHeader } = useAuth();
  const [data, setData] = useState(null);

  useEffect(() => {
    axios.get(`${API}/admin/onboarding-progress`, getAuthHeader())
      .then((res) => setData(res.data))
      .catch(() => setData({ agents: [], in_progress: 0, completed: 0, total_items: 11 }));
    // eslint-disable-next-line react-hooks/exhaustive-deps -- load once
  }, []);

  if (!data) return null;

  return (
    <div className="max-w-[1100px] mx-auto space-y-6" data-testid="onboarding-tracker-page">
      <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }}
        className="relative overflow-hidden rounded-[20px] bg-gradient-to-br from-[#0B1437] via-[#111C44] to-[#1B254B] p-7">
        <div className="pointer-events-none absolute -right-12 -top-14 h-48 w-48 rounded-full bg-[#4318FF]/35 blur-3xl" />
        <div className="relative flex flex-wrap items-center justify-between gap-4">
          <div className="flex items-center gap-4">
            <div className="flex h-13 w-13 shrink-0 items-center justify-center rounded-2xl bg-gradient-to-br from-[#4318FF] to-[#7000FF] p-3 shadow-lg">
              <ClipboardCheck className="h-6 w-6 text-white" strokeWidth={2} />
            </div>
            <div>
              <h1 className="font-['Manrope'] text-2xl font-extrabold tracking-tight text-white">Onboarding Tracker</h1>
              <p className="text-sm font-medium text-white/60">See exactly where every new agent is stuck — and who needs a nudge.</p>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <div className="rounded-2xl border border-white/10 bg-white/5 px-4 py-2.5 text-center">
              <p className="font-['Manrope'] text-xl font-extrabold text-[#F6C561]" data-testid="tracker-in-progress">{data.in_progress}</p>
              <p className="text-[10px] font-bold uppercase tracking-wider text-white/50">In Progress</p>
            </div>
            <div className="rounded-2xl border border-white/10 bg-white/5 px-4 py-2.5 text-center">
              <p className="font-['Manrope'] text-xl font-extrabold text-[#01B574]" data-testid="tracker-completed">{data.completed}</p>
              <p className="text-[10px] font-bold uppercase tracking-wider text-white/50">Completed</p>
            </div>
          </div>
        </div>
      </motion.div>

      {data.agents.length === 0 ? (
        <Card className="bg-white">
          <CardContent className="flex flex-col items-center gap-2 py-14 text-center">
            <Users className="h-8 w-8 text-[#A3AED0]" />
            <p className="text-sm font-semibold text-[#2B3674]">No onboarding agents yet</p>
            <p className="text-xs text-[#A3AED0]">Agents invited from now on will show up here with live checklist progress.</p>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-3">
          {data.agents.map((a, i) => {
            const pct = Math.round((a.checked_count / a.total_items) * 100);
            return (
              <motion.div key={a.id} initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.05 + i * 0.04 }}>
                <Card className="bg-white" data-testid={`tracker-agent-${a.id}`}>
                  <CardContent className="p-5">
                    <div className="flex flex-wrap items-center gap-4">
                      <div className={`flex h-11 w-11 shrink-0 items-center justify-center rounded-full text-sm font-extrabold text-white shadow-md ${
                        a.complete ? 'bg-gradient-to-br from-[#01B574] to-[#43E97B]' : 'bg-gradient-to-br from-[#4318FF] to-[#7000FF]'
                      }`}>
                        {initials(a.name)}
                      </div>
                      <div className="min-w-[180px] flex-1">
                        <div className="flex items-center gap-2">
                          <p className="text-sm font-extrabold text-[#2B3674]">{a.name}</p>
                          {a.complete ? (
                            <span className="inline-flex items-center gap-1 rounded-full bg-[#01B574]/10 px-2 py-0.5 text-[10px] font-bold text-[#01B574]">
                              <CheckCircle2 className="h-3 w-3" /> COMPLETE
                            </span>
                          ) : (
                            <span className="inline-flex items-center gap-1 rounded-full bg-amber-100 px-2 py-0.5 text-[10px] font-bold text-amber-700">
                              <Hourglass className="h-3 w-3" /> IN PROGRESS
                            </span>
                          )}
                        </div>
                        <p className="text-xs text-[#A3AED0]">
                          {a.email} · Upline: {a.upline_name} · Joined {a.joined_at ? new Date(a.joined_at).toLocaleDateString() : '—'}
                        </p>
                      </div>
                      <div className="w-full sm:w-64">
                        <div className="flex items-center justify-between text-[11px] font-bold text-[#2B3674]/70">
                          <span>{a.checked_count}/{a.total_items} steps</span>
                          <span>{pct}%</span>
                        </div>
                        <div className="mt-1 h-2 w-full overflow-hidden rounded-full bg-[#E9EDF7]">
                          <div
                            className={`h-full rounded-full transition-all ${a.complete ? 'bg-gradient-to-r from-[#01B574] to-[#43E97B]' : 'bg-gradient-to-r from-[#4318FF] to-[#7000FF]'}`}
                            style={{ width: `${pct}%` }}
                          />
                        </div>
                      </div>
                    </div>
                    {!a.complete && a.missing_items.length > 0 && (
                      <div className="mt-3 flex flex-wrap items-center gap-1.5 border-t border-[#E9EDF7] pt-3">
                        <span className="text-[11px] font-bold text-[#A3AED0]">Still missing:</span>
                        {a.missing_items.map((m) => (
                          <span key={m} className="rounded-md bg-[#F4F7FE] px-2 py-0.5 text-[11px] font-semibold text-[#2B3674]/75 border border-[#E9EDF7]">
                            {m}
                          </span>
                        ))}
                      </div>
                    )}
                  </CardContent>
                </Card>
              </motion.div>
            );
          })}
        </div>
      )}
    </div>
  );
}
